import { FEATURE_KEYS, OPERATION_IDS } from '../routes/ids.js';
import { PLATFORM_MODULE_ASSIGNMENT_FORM } from './platform-admin-live-descriptors.js';
function sourceField(name) {
    const field = PLATFORM_MODULE_ASSIGNMENT_FORM.fields.find((candidate) => candidate.name === name);
    if (!field)
        throw new Error(`Platform module assignment field ${name} is missing.`);
    return field;
}
function targetOrganizationField() {
    return {
        ...sourceField('organizationId'),
        id: 'field.platformModule.targetOrganizationId',
        name: 'targetOrganizationId',
    };
}
function outputField(field, name = field.name) {
    return {
        ...field,
        id: `field.platformModule.${name}`,
        name,
        fieldType: 'text',
        options: undefined,
        optionSource: undefined,
        validation: undefined,
        value: {
            ...(field.value ?? {}),
            hiddenValuePolicy: 'preserve',
            submitBehavior: 'exclude',
            outputOnly: true,
        },
    };
}
function hiddenSubmissionField(name) {
    return {
        id: `field.platformModule.${name}`,
        name,
        labelKey: PLATFORM_MODULE_ASSIGNMENT_FORM.titleKey,
        fieldType: 'hidden',
        value: {
            hiddenValuePolicy: 'preserve',
            submitBehavior: 'include',
            retainWhenHidden: true,
        },
    };
}
const cancelAction = PLATFORM_MODULE_ASSIGNMENT_FORM.actions.find((action) => action.kind === 'localUi');
const createSubmitAction = PLATFORM_MODULE_ASSIGNMENT_FORM.actions.find((action) => action.kind === 'command');
if (!cancelAction || !createSubmitAction) {
    throw new Error('Platform module assignment create form is missing canonical actions.');
}
const createOrganization = targetOrganizationField();
const createModule = sourceField('module');
const createMaxBranches = sourceField('maxBranches');
const createMaxUsers = sourceField('maxUsers');
const createEffectiveFrom = sourceField('effectiveFrom');
const createEffectiveTo = sourceField('effectiveTo');
const createGraceEndsAt = sourceField('graceEndsAt');
const createFields = [
    createOrganization,
    createModule,
    createMaxBranches,
    createMaxUsers,
    createEffectiveFrom,
    createEffectiveTo,
    createGraceEndsAt,
];
/**
 * Effective create form. `targetOrganizationId` is deliberately distinct from
 * the authenticated actor's `organizationId`, which the generic controller
 * always treats as server-owned. Platform Root selects a target organization;
 * it never supplies or overrides its own request scope.
 */
export const PLATFORM_MODULE_ASSIGNMENT_CREATE_FORM = Object.freeze({
    ...PLATFORM_MODULE_ASSIGNMENT_FORM,
    fields: createFields,
    sections: [{
            id: 'form.admin.platformModuleAssignment.create.section',
            titleKey: PLATFORM_MODULE_ASSIGNMENT_FORM.titleKey,
            fields: createFields.map((field) => field.id),
            layout: { columns: 2, gap: 16 },
        }],
    submission: {
        ...PLATFORM_MODULE_ASSIGNMENT_FORM.submission,
        includeFields: createFields.map((field) => field.id),
    },
});
const organizationName = outputField(sourceField('organizationId'), 'organizationName');
const moduleName = outputField(sourceField('module'));
const maxBranches = sourceField('maxBranches');
const maxUsers = sourceField('maxUsers');
const effectiveFrom = sourceField('effectiveFrom');
const effectiveTo = sourceField('effectiveTo');
const graceEndsAt = sourceField('graceEndsAt');
const expectedUpdatedAt = hiddenSubmissionField('expectedUpdatedAt');
const idempotencyKey = hiddenSubmissionField('idempotencyKey');
const editFields = [
    organizationName,
    moduleName,
    maxBranches,
    maxUsers,
    effectiveFrom,
    effectiveTo,
    graceEndsAt,
    expectedUpdatedAt,
    idempotencyKey,
];
const updateCommandId = 'cmd.admin.platform-modules.update';
const submitAction = {
    ...createSubmitAction,
    id: 'form.admin.platformModuleAssignment.edit.submit',
    telemetryId: 'experience.platform-admin.form.admin.platformModuleAssignment.edit.submit',
    accessibilityLabelKey: 'action.save',
    feature: FEATURE_KEYS.ADMIN_ROLES,
    operationId: OPERATION_IDS.PLATFORM_MODULES_ASSIGN,
    commandId: updateCommandId,
};
/**
 * Platform module assignment configuration form.
 *
 * Organization and module identity are server-owned. The hidden concurrency
 * token is loaded from the canonical record and submitted unchanged. The web
 * transport generates an idempotency key for each submission attempt.
 */
export const PLATFORM_MODULE_ASSIGNMENT_EDIT_FORM = Object.freeze({
    ...PLATFORM_MODULE_ASSIGNMENT_FORM,
    id: 'form.admin.platformModuleAssignment.edit',
    mode: 'edit',
    titleKey: 'page.moduleAssignments.title',
    fields: editFields,
    sections: [{
            id: 'form.admin.platformModuleAssignment.edit.section',
            titleKey: 'page.moduleAssignments.title',
            fields: editFields.map((field) => field.id),
            layout: { columns: 2, gap: 16 },
        }],
    actions: [cancelAction, submitAction],
    submission: {
        defaultCommandId: updateCommandId,
        includeFields: [
            maxBranches.id,
            maxUsers.id,
            effectiveFrom.id,
            effectiveTo.id,
            graceEndsAt.id,
            expectedUpdatedAt.id,
            idempotencyKey.id,
        ],
        successMessageKey: 'common.saveSuccess',
        errorMessageKey: 'common.error.unexpected',
    },
    operationId: OPERATION_IDS.PLATFORM_MODULES_ASSIGN,
});
export const PLATFORM_MODULE_ASSIGNMENT_FORM_REGISTRY = Object.freeze({
    [String(PLATFORM_MODULE_ASSIGNMENT_CREATE_FORM.id)]: PLATFORM_MODULE_ASSIGNMENT_CREATE_FORM,
    [String(PLATFORM_MODULE_ASSIGNMENT_EDIT_FORM.id)]: PLATFORM_MODULE_ASSIGNMENT_EDIT_FORM,
});
